import React, { useEffect, useRef, useState } from 'react';

const FLIP_KEYFRAMES = `
@keyframes jpro-flip-top {
  0%   { transform: rotateX(0deg); }
  100% { transform: rotateX(-90deg); }
}
@keyframes jpro-flip-bottom {
  0%   { transform: rotateX(90deg); }
  100% { transform: rotateX(0deg); }
}
`;

// FlipDigit — displays a single digit with a classic 3D split-flap animation
// when it changes.
//
// While idle: top + bottom halves both show `display` (the settled digit).
// When `digit` prop changes:
//   - Top static half immediately shows the NEW digit (will be revealed as the
//     falling flap clears it)
//   - Bottom static half keeps showing the OLD digit (display) until the
//     rising flap drops into place
//   - Top flap: OLD digit, rotates 0 → -90° (falls down, 1st half of animation)
//   - Bottom flap: NEW digit, rotates 90 → 0° (drops in, 2nd half of animation)
// After the timeout: `display` syncs to `digit` and the statics take over.
function FlipDigit({ digit, dark }) {
  const [display, setDisplay] = useState(digit);
  const [flipping, setFlipping] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (digit === display) return;
    setFlipping(true);
    timerRef.current = setTimeout(() => {
      setDisplay(digit);
      setFlipping(false);
    }, 500);
    return () => clearTimeout(timerRef.current);
  }, [digit, display]);

  const cardBg = dark
    ? 'linear-gradient(180deg, #1f2937 0%, #111827 100%)'
    : 'linear-gradient(180deg, #f9fafb 0%, #e5e7eb 100%)';
  const textColor = dark ? '#f9fafb' : '#1f2937';
  const hinge = dark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.15)';

  const Half = ({ value, half, style }) => (
    <div
      className="absolute inset-x-0 overflow-hidden flex justify-center"
      style={{
        top: half === 'top' ? 0 : '50%',
        bottom: half === 'bottom' ? 0 : '50%',
        alignItems: half === 'top' ? 'flex-end' : 'flex-start',
        background: cardBg,
        borderTopLeftRadius: half === 'top' ? 10 : 0,
        borderTopRightRadius: half === 'top' ? 10 : 0,
        borderBottomLeftRadius: half === 'bottom' ? 10 : 0,
        borderBottomRightRadius: half === 'bottom' ? 10 : 0,
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        ...style,
      }}
    >
      <span
        style={{
          color: textColor,
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
          fontWeight: 700,
          fontSize: 'inherit',
          lineHeight: 1,
          transform: half === 'top' ? 'translateY(50%)' : 'translateY(-50%)',
        }}
      >
        {value}
      </span>
    </div>
  );

  const topStatic = flipping ? digit : display;
  const bottomStatic = display;

  return (
    <div
      className="relative w-20 h-28 md:w-28 md:h-40 rounded-xl shadow-xl text-[3.25rem] md:text-[5rem]"
      style={{ perspective: '400px' }}
    >
      <Half value={topStatic} half="top" />
      <Half value={bottomStatic} half="bottom" />

      <div
        className="absolute inset-x-0 top-1/2 pointer-events-none z-20"
        style={{ height: 2, background: hinge, transform: 'translateY(-1px)' }}
      />

      {flipping && (
        <>
          <Half
            value={display}
            half="top"
            style={{
              transformOrigin: 'bottom',
              animation: 'jpro-flip-top 0.25s ease-in forwards',
              zIndex: 10,
              boxShadow: '0 6px 10px -6px rgba(0,0,0,0.4)',
            }}
          />
          <Half
            value={digit}
            half="bottom"
            style={{
              transformOrigin: 'top',
              animation: 'jpro-flip-bottom 0.25s 0.25s ease-out both',
              zIndex: 10,
              boxShadow: '0 -6px 10px -6px rgba(0,0,0,0.3) inset',
            }}
          />
        </>
      )}
    </div>
  );
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function FlipClock({ time, settings, dark }) {
  let hours = time.getHours();
  const isPM = hours >= 12;
  hours = hours % 12 || 12;
  const hh = String(hours).padStart(2, '0');
  const mm = String(time.getMinutes()).padStart(2, '0');
  const ss = String(time.getSeconds()).padStart(2, '0');

  return (
    <div className="flex flex-col items-center gap-4">
      <style>{FLIP_KEYFRAMES}</style>
      <div className="flex items-center gap-2 md:gap-3">
        <FlipDigit digit={hh[0]} dark={dark} />
        <FlipDigit digit={hh[1]} dark={dark} />
        <span className={`text-6xl md:text-7xl font-bold ${dark ? 'text-white/50' : 'text-gray-500/60'}`}>:</span>
        <FlipDigit digit={mm[0]} dark={dark} />
        <FlipDigit digit={mm[1]} dark={dark} />
        {settings.showSeconds && (
          <>
            <span className={`text-6xl md:text-7xl font-bold ${dark ? 'text-white/50' : 'text-gray-500/60'}`}>:</span>
            <FlipDigit digit={ss[0]} dark={dark} />
            <FlipDigit digit={ss[1]} dark={dark} />
          </>
        )}
        <span className={`ml-2 text-lg md:text-xl font-mono ${dark ? 'text-white/40' : 'text-gray-500/70'}`}>{isPM ? 'PM' : 'AM'}</span>
      </div>
      {settings.showDate && (
        <div className={`text-base font-rasa ${dark ? 'text-white/50' : 'text-gray-500/70'}`}>
          {DAYS[time.getDay()]}, {MONTHS[time.getMonth()]} {time.getDate()}, {time.getFullYear()}
        </div>
      )}
    </div>
  );
}
